import { useEffect, useState } from "react";

import { ChooseEntityConfig, useChooseEntityConfig } from "../choose-entity-config-context";
import { useFolderScope } from "../folder-scope-context-provider";

import ChooseEntityVariantView, { ChooseEntityVariantProvider } from "./ChooseEntityVariantView";
import { EntitySelection } from "./ChooseEntityVariantView/choose-entity-variant-context";
import { hasVersions } from "./utils";
import SmartTableSheetView from "./SmartTableSheetView";

import { Entity, EntityType, getEntityNumericId } from "@/queries";
import { getEntityName, getItemColor } from "@/components/UnifiedRegistry/utils";
import UnifiedRegistry from "@/components/UnifiedRegistry";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityIcon } from "@/components/ui/icons";

interface ChooseEntityViewSingleProps {
  type: EntityType;
  currentFolderId: number | null;
  readonly?: boolean;
  selectedEntity?: Entity;
  onSelect: (entity: Entity) => void;
  onClose: () => void;
}

export const ChooseEntityViewSingle = ({
  type,
  currentFolderId: propCurrentFolderId,
  readonly,
  selectedEntity,
  onSelect,
  onClose,
}: ChooseEntityViewSingleProps) => {
  const folderScope = useFolderScope();
  const currentScope = selectedEntity ? propCurrentFolderId : folderScope;
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(currentScope);
  const [locallySelected, setLocallySelected] = useState<Entity | undefined>();
  const [locallySelectedSmartTable, setLocallySelectedSmartTable] = useState<
    Entity<EntityType.SMART_TABLE> | undefined
  >();
  const { selectLatest, selectSmartTableSheet } = useChooseEntityConfig() as ChooseEntityConfig;

  useEffect(() => {
    setCurrentFolderId(currentScope);
  }, [currentScope]);

  const handleSelect = (entity: Entity) => {
    if (selectSmartTableSheet && entity.type === EntityType.SMART_TABLE) {
      setLocallySelectedSmartTable(entity as Entity<EntityType.SMART_TABLE>);
      return;
    }

    if (selectLatest || !hasVersions(entity)) {
      onSelect(entity);
      onClose();
    } else {
      setLocallySelected(entity);
    }
  };

  if (locallySelectedSmartTable) {
    return (
      <SmartTableSheetView
        table={locallySelectedSmartTable}
        readonly={readonly}
        onSelect={sheet => {
          onSelect({
            ...locallySelectedSmartTable,
            selected_sheet_id: sheet.id,
            selected_sheet_title: sheet.title,
          } as Entity);
          onClose();
        }}
        onOpenChange={open => {
          if (!open) {
            onClose();
          }
        }}
        onBack={() => setLocallySelectedSmartTable(undefined)}
      />
    );
  }

  if (locallySelected && hasVersions(locallySelected) && !selectLatest) {
    return (
      <ChooseEntityVariantProvider
        entity={locallySelected}
        selectedEntities={selectedEntity ? [selectedEntity] : []}
        readonly={readonly}
        onSelect={(selection: EntitySelection) => {
          if (selection.type === "version") {
            onSelect({
              ...locallySelected,
              version_number: selection.version,
            });
          } else {
            onSelect({
              ...locallySelected,
              label: selection.label,
            });
          }
        }}
        onOpenChange={open => {
          if (!open) {
            onClose();
          }
        }}
        onBack={() => setLocallySelected(undefined)}
      >
        <ChooseEntityVariantView />
      </ChooseEntityVariantProvider>
    );
  }

  return (
    <>
      <DialogHeader className="border-b border-gray-200 px-6 py-4">
        <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
          <div
            className={`flex h-10 w-full max-w-[40px] flex-shrink-0 items-center justify-center rounded-full ${getItemColor(
              type,
            )}`}
          >
            <EntityIcon type={type} size="md" />
          </div>
          <span className="min-w-0 flex-1">{`Select ${getEntityName(type)}`}</span>
        </DialogTitle>
        {type === EntityType.DATASET && (
          <DialogDescription className="text-sm text-gray-600">
            Datasets provide test cases for your evaluation. Each row will be run in batch to test
            your prompts or workflows.
          </DialogDescription>
        )}
      </DialogHeader>

      <UnifiedRegistry
        currentFolderId={currentFolderId}
        onItemClick={item => {
          if (readonly) {
            return;
          }
          if (item.type === EntityType.FOLDER) {
            setCurrentFolderId(getEntityNumericId(item.id) ?? null);
          } else {
            handleSelect(item);
          }
        }}
      />
    </>
  );
};

export default ChooseEntityViewSingle;
